import { Swiper, SwiperSlide } from 'swiper/react';
import { useTranslation, Trans } from 'react-i18next';
import 'swiper/css';

import ic1 from '../../../assets/assets-home/svg/whyus/1.svg';
import ic2 from '../../../assets/assets-home/svg/whyus/2.svg';
import ic3 from '../../../assets/assets-home/svg/whyus/3.svg';
import ic4 from '../../../assets/assets-home/svg/whyus/4.svg';

type Item = { icon: string; textKey: string };

const items: Item[] = [
  { icon: ic1, textKey: 'whyUs.items.confidentiality' },
  { icon: ic2, textKey: 'whyUs.items.access' },
  { icon: ic3, textKey: 'whyUs.items.vip' },
  { icon: ic4, textKey: 'whyUs.items.availability' },
];

const cardStyle = {
  borderWidth: '1px 1px 0px 1px',
  borderColor: '#DBDBDB',
  borderStyle: 'solid' as const,
  boxShadow: '0px 5px 0px rgba(0,0,0,0.15)',
};

function WhyUsCard({ icon, text, isRu }: { icon: string; text: string; isRu: boolean }) {
  return (
    <div
      className="min-h-[167px] sm:min-h-[227px] rounded-[20px] flex flex-col items-center gap-6 sm:gap-8 px-4 py-6 sm:py-8"
      style={cardStyle}
    >
      <div className="bg-[#EFE8DF] rounded-[9px] sm:rounded-xl flex items-center justify-center w-[53px] h-[50px] sm:w-[70px] sm:h-[65px]">
        <img src={icon} className="w-[30px] h-[30px] sm:w-auto sm:h-auto" />
      </div>

      <p className={`text-xs sm:text-lg text-black lg:text-[#4A4A4A] text-center leading-tight lg:max-w-full max-w-[327px] ${isRu ? 'font-normal' : 'lg:font-normal font-medium'}`}>
        {text}
      </p>
    </div>
  );
}

export default function WhyUs() {
  const { t, i18n } = useTranslation('home');
  const isRu = i18n.language === 'ru';
  return (
    <div className="max-w-[1600px] py-8 sm:py-10 lg:py-[60px] mx-auto" id="why_us">
      <h2 className="section-heading text-center lg:mb-4 mb-2 sm:hidden">{t('whyUs.headingMobile')}</h2>
      <h2 className="section-heading text-center lg:mb-4 mb-2 hidden sm:block">{t('whyUs.heading')}</h2>

      <div className='text-xs sm:text-lg text-[#4A4A4A] text-center leading-snug mb-6 sm:mb-8 lg:mb-11 mx-auto px-5 sm:px-6 max-w-[914px]'>
        <p className='m-0'>
          <Trans
            i18nKey="whyUs.intro.line1"
            ns="home"
            components={{ strong1: <span className='font-semibold' /> }}
          />
        </p>
        <p className='m-0 sm:block hidden'>
          <Trans
            i18nKey="whyUs.intro.line2"
            ns="home"
            components={{ br1: <br className='lg:hidden' /> }}
          />
        </p>
        <p className='m-0'>
          <Trans
            i18nKey="whyUs.intro.line3"
            ns="home"
            components={{
              br1: <br className='hidden sm:block lg:hidden' />,
              br2: <br className='hidden sm:block' />,
            }}
          />
        </p>
      </div>

      {/* Mobile swiper - below sm */}
      <div className="sm:hidden overflow-x-clip">
        <Swiper
          slidesPerView='auto'
          spaceBetween={20}
          style={{ overflow: 'visible' }}
          slidesOffsetBefore={20}
          slidesOffsetAfter={20}
          breakpoints={{
            640: { slidesOffsetBefore: 24, slidesOffsetAfter: 24 }
          }}
        >
          {items.map((item, i) => (
            <SwiperSlide key={i} className='w-[242px]'>
              <WhyUsCard icon={item.icon} text={t(item.textKey)} isRu={isRu} />
            </SwiperSlide>
          ))}
        </Swiper>
      </div>

      {/* Grid - sm and above */}
      <div className='hidden sm:grid lg:grid-cols-2 xl:grid-cols-4 gap-5 px-10'>
        {items.map((item, i) => (
          <WhyUsCard key={i} icon={item.icon} text={t(item.textKey)} isRu={isRu} />
        ))}
      </div>
    </div>
  );
}
